"use client";

import { Check, Loader2 } from "lucide-react";
import { FOLLOW_LOCATIONS } from "@punchup/data/legacy";
import {
  useVenueSubscription,
  useVenueSubscriptionMutation,
} from "../../hooks/venuePages/useVenueSubscription";

import CheckoutModalOrDrawerVenue from "./CheckoutModalOrDrawerVenue";
import { VenuePagesRow } from "../../data/venue_pages";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { logException } from "@punchup/sentry";
import { toast } from "@punchup/ui-common/shadcn/ui/use-toast";
import { useState } from "react";
import { useVenuePageTheme } from "../../hooks";

interface Props {
  venue: VenuePagesRow;
  isUserSubscribed: boolean;
  userEmail?: string;
}

export default function VenuePageFooterSubscribeForm({
  venue,
  isUserSubscribed,
  userEmail,
}: Props) {
  const [email, setEmail] = useState(userEmail || "");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { isFollowing } = useVenueSubscription(venue.id, isUserSubscribed);
  const mutation = useVenueSubscriptionMutation(venue);
  const supabase = createClientComponentClient();
  const { accentColor, buttonLabelColor } = useVenuePageTheme(venue.slug);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user?.id) {
        setShowLoginModal(true);
        return;
      }

      mutation.mutate(
        { action: "follow", location: FOLLOW_LOCATIONS.VENUE_PAGE },
        {
          onSuccess: () => {
            toast({
              title: `Subscribed to ${venue.name}`,
              variant: "success",
            });
          },
          onError: (error) => {
            logException(error);
            toast({
              title: "Error",
              description: "Couldn't subscribe. Please try again.",
              variant: "destructive",
            });
          },
        }
      );
    } catch (error) {
      logException(error);
    }
  };

  if (isFollowing) {
    return (
      <p className="inline-flex items-center gap-2 text-[14px] text-[#4A4A4A]">
        <Check
          width={16}
          height={16}
          strokeWidth={2.5}
          className="text-green-600"
        />
        You&apos;re on the guest list.
      </p>
    );
  }

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="flex flex-col sm:flex-row gap-2 max-w-md"
      >
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          disabled={mutation.isPending}
          className="flex-1 px-3 py-2 text-[14px] rounded-md border border-[#D9D9D9] bg-white text-[#29333C] placeholder:text-[#9A9A9A] focus:outline-none focus:ring-2 focus:ring-[#29333C]/20"
        />
        <button
          type="submit"
          disabled={mutation.isPending || !email.trim()}
          style={{
            backgroundColor: accentColor,
            color: buttonLabelColor,
          }}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-[14px] font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {mutation.isPending ? (
            <Loader2 width={16} height={16} className="animate-spin" />
          ) : (
            "Subscribe"
          )}
        </button>
      </form>
      <CheckoutModalOrDrawerVenue
        isOpen={showLoginModal}
        setIsOpen={setShowLoginModal}
        venueToFollow={venue}
        redirectTo={
          process.env.NEXT_PUBLIC_IS_EXTERNAL_SITE ? "/" : `/v/${venue.slug}`
        }
        userEmail={email}
      />
    </>
  );
}
